import { clearingApi } from "../../config/apiConfig";

const BASE = "api/v1/thg/payouts";

/**
 * CLR-283 THG payout credit notes per station and year: generate the draft, open it, and re-read the year's
 * statuses from the accounting system. Finalising and sending is done in the accounting system itself, not
 * from the Back Office; the sync picks up the result.
 *
 * Every refusal is returned as a named `kind`, because each has its own remedy:
 * - 503: the accounting system is rate limited (10 requests/min app-wide). Try again in a moment.
 * - 502: the accounting system rejected the request.
 * - 422: the station is not eligible; `reason` says why (NOTHING_TO_PAY, NO_OWNER, NO_BANK_DETAILS).
 * - 409: already active, changed by another request, or the year is not FINAL.
 * - 404: no credit note for this station and year.
 * - 401/403: not an admin. 400: malformed year.
 * No function throws, and none retries: a retry would spend the shared accounting-system budget.
 */
const KIND_BY_STATUS = {
  400: "BAD_REQUEST",
  401: "FORBIDDEN",
  403: "FORBIDDEN",
  404: "NOT_FOUND",
  409: "CONFLICT",
  422: "NOT_ELIGIBLE",
  502: "ACCOUNTING_REJECTED",
  503: "ACCOUNTING_BUSY",
};

/** @typedef {import("../../types/thgAnnualSummary").ThgPayoutFailure} ThgPayoutFailure */
/** @typedef {import("../../types/thgAnnualSummary").ThgStationPayout} ThgStationPayout */
/** @typedef {import("../../types/thgAnnualSummary").ThgPayoutSyncResult} ThgPayoutSyncResult */
/** @typedef {import("../../types/thgAnnualSummary").ThgPdfLink} ThgPdfLink */

/** @returns {ThgPayoutFailure} */
const toThgPayoutFailure = (error, context, fallback) => {
  console.error(`Failed to ${context}:`, error);
  const status = error?.response?.status ?? null;
  const body = error?.response?.data;
  return {
    success: false,
    kind: KIND_BY_STATUS[status] ?? "UNKNOWN",
    message: body?.error ?? fallback,
    reason: body?.reason ?? null,
    status,
  };
};

const stationPath = (year, stationId) => `${BASE}/${year}/${encodeURIComponent(stationId)}/credit-note`;

/** @returns {Promise<import("../../types/thgAnnualSummary").ThgPayoutResult<ThgStationPayout>>} */
export const generateThgCreditNote = async (year, stationId) => {
  try {
    const response = await clearingApi.post(stationPath(year, stationId));
    return { success: true, data: response?.data };
  } catch (error) {
    return toThgPayoutFailure(
      error,
      `generate the THG credit note for ${stationId}/${year}`,
      `Could not generate the credit note for ${stationId}.`,
    );
  }
};

/**
 * A short-lived (5 minute) link to the credit note PDF. Fetch it on every click; never keep it.
 * @returns {Promise<import("../../types/thgAnnualSummary").ThgPayoutResult<ThgPdfLink>>}
 */
export const getThgCreditNotePdfUrl = async (year, stationId) => {
  try {
    const response = await clearingApi.get(`${stationPath(year, stationId)}/pdf`);
    return { success: true, data: response?.data };
  } catch (error) {
    return toThgPayoutFailure(
      error,
      `get the THG credit note PDF for ${stationId}/${year}`,
      `Could not open the credit note for ${stationId}.`,
    );
  }
};

/** @returns {Promise<import("../../types/thgAnnualSummary").ThgPayoutResult<ThgPayoutSyncResult>>} */
export const syncThgPayouts = async (year) => {
  try {
    const response = await clearingApi.post(`${BASE}/${year}/sync`);
    return { success: true, data: response?.data };
  } catch (error) {
    return toThgPayoutFailure(
      error,
      `sync the THG credit notes for ${year}`,
      `Could not sync the THG credit notes for ${year}.`,
    );
  }
};
